import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { getShellMuiTheme } from 'components/AppShell/muiTheme';
import { useThemeMode } from 'components/AppShell/ThemeMode';

const SnackbarContext = createContext();

let snackbarRef = null;
export function setGlobalSnackbar(fn) {
  snackbarRef = fn;
}
export function globalSnackbar(args) {
  if (snackbarRef) snackbarRef(args);
  else if (typeof window !== 'undefined') {
    // fallback: alert for dev
    alert(args.message);
  }
}

const ConfirmContext = createContext();
let confirmRef = null;
export function setGlobalConfirm(fn) {
  confirmRef = fn;
}
export function globalConfirm({
  message,
  onConfirm,
  onCancel,
  confirmText = 'OK',
  cancelText = 'Отмена',
  title = 'Подтверждение',
}) {
  if (confirmRef) confirmRef({ message, onConfirm, onCancel, confirmText, cancelText, title });
  else if (typeof window !== 'undefined') {
    if (window.confirm(message)) onConfirm && onConfirm();
    else onCancel && onCancel();
  }
}
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a GlobalConfirmProvider');
  return ctx.showConfirm;
}
export function GlobalConfirmProvider({ children }) {
  const [state, setState] = React.useState({ open: false });
  const handleClose = () => {
    setState({ open: false });
    state.onCancel && state.onCancel();
  };
  const handleConfirm = () => {
    setState({ open: false });
    state.onConfirm && state.onConfirm();
  };
  const showConfirm = React.useCallback((opts) => setState({ ...opts, open: true }), []);
  React.useEffect(() => {
    setGlobalConfirm(showConfirm);
    return () => setGlobalConfirm(null);
  }, [showConfirm]);
  const { mode } = useThemeMode();
  const muiTheme = useMemo(() => getShellMuiTheme(mode), [mode]);

  return (
    <ConfirmContext.Provider value={{ showConfirm }}>
      {children}
      <ThemeProvider theme={muiTheme}>
        <Dialog
          open={!!state.open}
          onClose={handleClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 'var(--shell-radius-lg)',
              border: '1px solid var(--shell-border-subtle)',
              boxShadow: 'var(--shell-shadow-overlay)',
              fontFamily:
                "'Inter', 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
            },
          }}
        >
          <DialogTitle sx={{ fontSize: 16, fontWeight: 600, pb: 1, pt: 2.5, px: 2.5 }}>
            {state.title || 'Подтверждение'}
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, pb: 0 }}>
            <Typography sx={{ fontSize: 13, color: 'var(--shell-text-secondary)' }}>
              {state.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, pt: 2, gap: 0.5 }}>
            <Button onClick={handleClose} variant="text">
              {state.cancelText || 'Отмена'}
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{
                background: 'var(--shell-semantic-error)',
                '&:hover': { background: '#991b1b' },
              }}
            >
              {state.confirmText || 'OK'}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </ConfirmContext.Provider>
  );
}

export function GlobalSnackbarProvider({ children }) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback(
    ({ message, severity = 'info', duration = 3000, autoClose = true }) => {
      setSnackbar({ open: true, message, severity, duration, autoClose });
    },
    []
  );

  const handleClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  React.useEffect(() => {
    setGlobalSnackbar(showSnackbar);
    return () => setGlobalSnackbar(null);
  }, [showSnackbar]);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoClose ? snackbar.duration || 3000 : null}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            whiteSpace: 'pre-line',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within a GlobalSnackbarProvider');
  return ctx.showSnackbar;
}
